'use client'

// ---------------------------------------------------------------------------
// Ghost mode — an admin (long@ / koh@) is viewing a customer project or
// workspace library without joining it or appearing in presence.
//
// The session is started from the backstage projects page (which calls the
// backend, writes the access log, and gets the target workspace org), then the
// admin is navigated to /p/<projectId>. The project page reads this store to
// take a ghost open path (no join, no member row) and to point the library
// panel at `targetOrgId` instead of the admin's own 'personal' workspace.
//
// Persisted to sessionStorage (not local): it must survive the navigation to
// /p/<projectId> and a hard reload of that tab, but a ghost session should not
// leak into other tabs or outlive the browser session.
// ---------------------------------------------------------------------------

import { create } from 'zustand'

const KEY = 'martini-ghost-session'
const LIBRARY_KEY = 'martini-library-ghost-session'
export type GhostMode = 'read' | 'write'

export interface GhostSession {
  projectId: string
  projectName: string
  /** Workspace org whose library the admin can read/populate. */
  targetOrgId: string
  targetOrgName: string
  targetIsTeam: boolean
  /** Project fields needed to build a ProjectWithRole without a membership row. */
  teamId: string | null
  visibility: string
  /** Defaults to read-only; write mode is enabled explicitly and audited. */
  mode: GhostMode
}

export interface LibraryGhostSession {
  /** Workspace org whose library the admin can read/populate. */
  targetOrgId: string
  targetOrgName: string
  targetIsTeam: boolean
  /** Defaults to read-only; write mode is enabled explicitly and audited. */
  mode: GhostMode
}

function readStoredSession<T extends { mode: GhostMode }>(
  key: string,
  normalize: (value: Record<string, unknown> & { mode?: GhostMode }) => T,
): T | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, unknown> & { mode?: GhostMode }
    return normalize(parsed)
  } catch {
    return null
  }
}

function readStored(): GhostSession | null {
  return readStoredSession(KEY, parsed => ({
    ...(parsed as Omit<GhostSession, 'mode'>),
    mode: parsed.mode === 'write' ? 'write' : 'read',
  }))
}

function readStoredLibrary(): LibraryGhostSession | null {
  return readStoredSession(LIBRARY_KEY, parsed => ({
    ...(parsed as Omit<LibraryGhostSession, 'mode'>),
    mode: parsed.mode === 'write' ? 'write' : 'read',
  }))
}

interface GhostModeState {
  session: GhostSession | null
  enter: (session: Omit<GhostSession, 'mode'> & { mode?: GhostMode }) => void
  setMode: (mode: GhostMode) => void
  exit: () => void
}

export const useGhostMode = create<GhostModeState>(set => ({
  // Seed synchronously from sessionStorage so the project page sees the session
  // on first render (avoids a flash of the access-denied bouncer on reload).
  session: readStored(),
  enter: session => {
    const next = { ...session, mode: session.mode ?? 'read' }
    if (typeof window !== 'undefined') sessionStorage.setItem(KEY, JSON.stringify(next))
    set({ session: next })
  },
  setMode: mode => {
    set(state => {
      if (!state.session) return state
      const session = { ...state.session, mode }
      if (typeof window !== 'undefined') sessionStorage.setItem(KEY, JSON.stringify(session))
      return { session }
    })
  },
  exit: () => {
    if (typeof window !== 'undefined') sessionStorage.removeItem(KEY)
    set({ session: null })
  },
}))

interface LibraryGhostModeState {
  session: LibraryGhostSession | null
  enter: (session: Omit<LibraryGhostSession, 'mode'> & { mode?: GhostMode }) => void
  setMode: (mode: GhostMode) => void
  exit: () => void
}

export const useLibraryGhostMode = create<LibraryGhostModeState>(set => ({
  session: readStoredLibrary(),
  enter: session => {
    const next = { ...session, mode: session.mode ?? 'read' }
    if (typeof window !== 'undefined') sessionStorage.setItem(LIBRARY_KEY, JSON.stringify(next))
    set({ session: next })
  },
  setMode: mode => {
    set(state => {
      if (!state.session) return state
      const session = { ...state.session, mode }
      if (typeof window !== 'undefined') sessionStorage.setItem(LIBRARY_KEY, JSON.stringify(session))
      return { session }
    })
  },
  exit: () => {
    if (typeof window !== 'undefined') sessionStorage.removeItem(LIBRARY_KEY)
    set({ session: null })
  },
}))

/** Non-reactive read for imperative code (yjs awareness, bootstrap). */
export function getGhostSessionForProject(projectId: string): GhostSession | null {
  const session = useGhostMode.getState().session
  return session && session.projectId === projectId ? session : null
}

export function getActiveGhostSession(): GhostSession | null {
  return useGhostMode.getState().session
}

export function getActiveLibraryGhostSession(): LibraryGhostSession | null {
  return useLibraryGhostMode.getState().session
}

export function getGhostWriteRequestHeaders(workspaceId?: string): Record<string, string> {
  const librarySession = getActiveLibraryGhostSession()
  if (librarySession?.mode === 'write' && (!workspaceId || librarySession.targetOrgId === workspaceId)) {
    return {
      'x-martini-ghost-mode': 'write',
      'x-martini-ghost-target-org-id': librarySession.targetOrgId,
    }
  }

  const session = getActiveGhostSession()
  if (!session || session.mode !== 'write' || (workspaceId && session.targetOrgId !== workspaceId)) return {}
  return {
    'x-martini-ghost-mode': 'write',
    'x-martini-ghost-project-id': session.projectId,
    'x-martini-ghost-target-org-id': session.targetOrgId,
  }
}
